import { Modal, Form, Input, Select, DatePicker, Button, Row, Col, Tag, Spin, Upload, Image, message } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, UserOutlined, CameraOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { schoolApi } from '../../../api/school.api';
import { childApi }  from '../../../api/child.api';
import { GENDER, USER_ROLES } from '../../../utils/constants';
import { useAuthStore } from '../../../store/auth.store';
import { resolveFileUrl } from '../../../utils/helpers';
import dayjs from 'dayjs';

const getRecordId = (record) => record?.id || record?._id || (typeof record === 'string' ? record : undefined);

// ─── Age calculator ───────────────────────────────────────────────
const calcAge = (dob) => {
  if (!dob) { return null; }
  const today = dayjs();
  const birth = dayjs(dob);
  let years = today.year() - birth.year();
  const m = today.month() - birth.month() + (today.date() < birth.date() ? -1 : 0);
  const remainMonths = ((m % 12) + 12) % 12;
  if (today.month() < birth.month() || (today.month() === birth.month() && today.date() < birth.date())) {
    years -= 1;
  }
  return { years: Math.max(0, years), months: remainMonths };
};

const AgeDisplay = ({ dob }) => {
  const age = calcAge(dob);
  if (!age) { return null; }
  const { years, months } = age;
  const label = years > 0
    ? `${years} yr${years !== 1 ? 's' : ''}${months > 0 ? ` ${months} mo` : ''}`
    : `${months} month${months !== 1 ? 's' : ''}`;
  // return (
  //   <Tag
  //     color="purple"
  //     icon={<CalendarOutlined />}
  //     style={{ marginLeft: 8, borderRadius: 9999, fontSize: 12, fontWeight: 600 }}
  //   >
  //     Age: {label}
  //   </Tag>
  // );
};

// ─── Shared form body (used by both modal and embedded modes) ─────
const STANDARD_OPTIONS = [
  'Nursery', 'LKG', 'UKG',
  '1st', '2nd', '3rd', '4th', '5th',
  '6th', '7th', '8th', '9th', '10th',
  '11th', '12th',
];

const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);

const resolveImageSrc = resolveFileUrl;

const ChildPhotoUploadItem = ({ file, onPreview, onEdit, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative', width: 90, height: 90, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={resolveImageSrc(file.url || file.thumbUrl)}
        alt="child"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {hovered && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
        }}>
          <EyeOutlined    style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onPreview(); }} />
          <EditOutlined   style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onEdit(); }} />
          <DeleteOutlined style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onRemove(); }} />
        </div>
      )}
    </div>
  );
};

const ChildFormBody = ({
  form, hideSchool, schoolReadOnly, schoolName, schoolOptions, schoolsLoading,
  selectedDob, setSelectedDob, autoFillStatus, handleParentPhoneChange,
  fileList, fileInputRef, previewOpen, previewSrc, setPreviewOpen,
  beforeUpload, handleDrop, handlePreview, onFileSelect, onRemoveFile,
}) => (
  <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
    {/* ── Child Photo ──────────────────────────────────────────── */}
    <Row gutter={16}>
      <Col span={24}>
        <Form.Item label="Child Photo" extra="JPG or PNG · Max 5 MB">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files[0];
              if (f) { onFileSelect(f); }
              e.target.value = '';
            }}
          />
          <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
            <Upload
              listType="picture-card"
              fileList={fileList}
              beforeUpload={beforeUpload}
              onRemove={onRemoveFile}
              onPreview={handlePreview}
              maxCount={1}
              accept="image/jpeg,image/jpg,image/png"
              itemRender={(_, file, __, { remove }) => (
                <ChildPhotoUploadItem
                  file={file}
                  onPreview={() => handlePreview(file)}
                  onEdit={() => fileInputRef.current?.click()}
                  onRemove={remove}
                />
              )}
            >
              {fileList.length === 0 && (
                <div>
                  <CameraOutlined style={{ fontSize: 22, color: 'var(--wl-text-muted)' }} />
                  <div style={{ fontSize: 10, color: 'var(--wl-text-muted)', marginTop: 4, textAlign: 'center', lineHeight: 1.3 }}>
                    Upload<br />Photo
                  </div>
                </div>
              )}
            </Upload>
          </div>
          <Image
            style={{ display: 'none' }}
            preview={{
              visible: previewOpen,
              src: previewSrc,
              onVisibleChange: (v) => setPreviewOpen(v),
            }}
          />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item name="name" label="Child Name" rules={[{ required: true }, { min: 2, message: 'Name must be at least 2 characters' }, { max: 100, message: 'Name cannot exceed 100 characters' }, { pattern: /^[a-zA-Z\s'\-.]+$/, message: 'Name can only contain letters, spaces, hyphens, and apostrophes' }]}>
          <Input placeholder="Enter child name" maxLength={100} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
          <Select placeholder="Select gender">
            {Object.entries(GENDER).map(([k, v]) => (
              <Select.Option key={v} value={v}>{k}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          name="dateOfBirth"
          label={<span>Date of Birth <AgeDisplay dob={selectedDob} /></span>}
          rules={[{ required: true, message: 'Date of birth is required' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={(d) => d && d > dayjs()}
            format="DD/MM/YYYY"
            placeholder="Select DOB"
            onChange={(date) => setSelectedDob(date)}
          />
        </Form.Item>
      </Col>

      {!hideSchool && (
        <Col span={12}>
          {schoolReadOnly ? (
            <>
              <Form.Item name="school" hidden rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="School">
                <Input disabled value={schoolName} placeholder={schoolsLoading ? 'Loading school...' : 'School not found'} />
              </Form.Item>
            </>
          ) : (
            <Form.Item name="school" label="School" rules={[{ required: true }]}>
              <Select
                placeholder="Select school"
                showSearch
                optionFilterProp="children"
                loading={schoolsLoading}
              >
                {schoolOptions.map((s) => (
                  <Select.Option key={s.value} value={s.value}>{s.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>
      )}

      <Col span={12}>
        <Form.Item name="standard" label="Standard / Class">
          <Select placeholder="Select standard" allowClear>
            {STANDARD_OPTIONS.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="section" label="Section" rules={[{ max: 200, message: 'Max 200 characters' }]}>
          <Input placeholder="e.g. A, B, C" maxLength={200} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="hobbies" label="Hobbies" rules={[{ max: 200, message: 'Max 200 characters' }]}>
          <Input.TextArea rows={2} placeholder="e.g. Drawing, Cricket" maxLength={200} showCount />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="favouriteFood" label="Favourite Food" rules={[{ max: 200, message: 'Max 200 characters' }]}>
          <Input.TextArea rows={2} placeholder="e.g. Rice, Mango" maxLength={200} showCount />
        </Form.Item>
      </Col>
    </Row>

    {/* ── Parent / Guardian ──────────────────────────────────── */}
    <div style={{ borderTop: '2px solid var(--wl-primary-pale)', paddingTop: 16, marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)', fontSize: 14 }}>
          Parent / Guardian Details
        </span>
        {autoFillStatus === 'loading' && (
          <Tag icon={<Spin size="small" />} color="processing" style={{ borderRadius: 9999 }}>
            Looking up…
          </Tag>
        )}
        {autoFillStatus === 'filled' && (
          <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 9999, fontSize: 11 }}>
            Auto-filled from existing record
          </Tag>
        )}
        {autoFillStatus === 'different-parent' && (
          <Tag icon={<ExclamationCircleOutlined />} color="warning" style={{ borderRadius: 9999, fontSize: 11 }}>
            This number belongs to a different existing parent — you'll be asked to confirm on save
          </Tag>
        )}
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="parent.phone"
            label="Mobile Number"
            getValueFromEvent={onlyDigits(10)}
            rules={[{ required: true }, { pattern: /^[1-9]\d{9}$/, message: 'Must be 10 digits and cannot start with 0' }]}
            extra="Entering a known mobile auto-fills parent details"
          >
            <Input
              placeholder="10-digit mobile"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              onChange={handleParentPhoneChange}
              prefix={<UserOutlined style={{ color: 'var(--wl-text-muted)' }} />}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="parent.name" label="Parent Name" rules={[{ required: true }, { min: 2, message: 'Name must be at least 2 characters' }, { max: 100, message: 'Name cannot exceed 100 characters' }, { pattern: /^[a-zA-Z\s'\-.]+$/, message: 'Name can only contain letters, spaces, hyphens, and apostrophes' }]}>
            <Input placeholder="Parent name" maxLength={100} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="parent.email" label="Email" rules={[{ type: 'email' }]}>
            <Input placeholder="Parent email" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="parent.address" label="Parent Address" rules={[{ validator: (_, val) => (!val || val.length >= 15) ? Promise.resolve() : Promise.reject(new Error('Address must be at least 15 characters')) }]}>
            <Input.TextArea rows={2} placeholder="Parent address" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  </Form>
);

// ─── Main Form Component ──────────────────────────────────────────
// Props:
//   asModal    (default true)  — wraps in Modal; false = inline with submit button
//   hideSchool (default false) — hides school field (caller injects school)
//   submitText                 — overrides submit button label in embedded mode
//   resetKey                   — increment to imperatively reset the embedded form
const ChildForm = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  loading,
  asModal    = true,
  hideSchool = false,
  submitText,
  resetKey,
}) => {
  const [form]               = Form.useForm();
  const [selectedDob,        setSelectedDob]        = useState(null);
  const [autoFillStatus,     setAutoFillStatus]     = useState(null);
  const [lastSearchedPhone,  setLastSearchedPhone]  = useState('');
  const [fileList,           setFileList]           = useState([]);
  const [previewSrc,         setPreviewSrc]         = useState('');
  const [previewOpen,        setPreviewOpen]        = useState(false);
  const [sharedParentContext, setSharedParentContext] = useState(null);
  const fileInputRef = useRef(null);
  const { user } = useAuthStore();
  const userSchoolId = getRecordId(user?.school);
  const isSchoolStaff = user?.role === USER_ROLES.SCHOOL_STAFF;
  const schoolReadOnly = !hideSchool && isSchoolStaff && !!userSchoolId;

  const { data: schoolsData, isFetching: schoolsLoading } = useQuery({
    queryKey: ['schools-all'],
    queryFn:  () => schoolApi.getAll({ limit: 200 }),
    enabled:  !hideSchool && (asModal ? open : true),
  });
  const allSchoolOptions = (schoolsData?.data || []).map((s) => ({ value: getRecordId(s), label: s.name }));
  const schoolName = user?.school?.name
    || initialValues?.school?.name
    || allSchoolOptions.find((s) => s.value === userSchoolId)?.label
    || '';
  const schoolOptions = schoolReadOnly ? [] : allSchoolOptions;

  // Populate or reset whenever the form becomes visible / initialValues change
  useEffect(() => {
    const shouldRun = asModal ? open : true;
    if (!shouldRun) { return; }

    if (initialValues) {
      const dob = initialValues.dateOfBirth ? dayjs(initialValues.dateOfBirth) : null;
      form.setFieldsValue({
        ...initialValues,
        school:           schoolReadOnly ? userSchoolId : getRecordId(initialValues.school),
        dateOfBirth:      dob,
        'parent.name':    initialValues.parent?.name,
        'parent.phone':   initialValues.parent?.phone,
        'parent.email':   initialValues.parent?.email,
        'parent.address': initialValues.parent?.address,
      });
      setSelectedDob(dob);
      setFileList(
        initialValues.childImage
          ? [{ uid: '-1', name: 'photo', status: 'done', url: initialValues.childImage }]
          : [],
      );
    } else {
      form.resetFields();
      setSelectedDob(null);
      setFileList([]);
      if (schoolReadOnly) {
        form.setFieldValue('school', userSchoolId);
      }
    }
    setAutoFillStatus(null);
    setLastSearchedPhone('');
  }, [open, initialValues, form, asModal, schoolReadOnly, userSchoolId]);

  // Imperative reset for embedded mode (caller increments resetKey)
  useEffect(() => {
    if (resetKey == null) { return; }
    form.resetFields();
    if (schoolReadOnly) {
      form.setFieldValue('school', userSchoolId);
    }
    setSelectedDob(null);
    setFileList([]);
    setAutoFillStatus(null);
    setLastSearchedPhone('');
  }, [resetKey, form, schoolReadOnly, userSchoolId]);

  const isEdit = !!initialValues;

  // ── Image upload handlers ───────────────────────────────────────
  const validateAndSetImage = useCallback((file) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      message.error('Only JPG and PNG images are allowed');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('Image must be smaller than 5 MB');
      return false;
    }
    const url = URL.createObjectURL(file);
    setFileList([{ uid: file.uid ?? `upload-${Date.now()}`, name: file.name, status: 'done', originFileObj: file, url }]);
    return true;
  }, []);

  const handleImageBeforeUpload = useCallback((file) => {
    validateAndSetImage(file);
    return Upload.LIST_IGNORE; // always suppress AntD's own upload + list-add
  }, [validateAndSetImage]);

  const handleImageDrop = useCallback((e) => {
    e.preventDefault();
    if (fileList.length === 0) { return; } // AntD handles drops on its own button when slot is empty
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) { validateAndSetImage(droppedFile); }
  }, [fileList, validateAndSetImage]);

  const handleImagePreview = useCallback(async (file) => {
    let src = file.url;
    if (!src && file.originFileObj) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    setPreviewSrc(resolveImageSrc(src));
    setPreviewOpen(true);
  }, []);

  const handleImageRemove = useCallback(() => {
    setFileList([]);
  }, []);

  // ── Parent phone auto-fill ────────────────────────────────────
  const handleParentPhoneChange = useCallback(async (e) => {
    const phone = e.target.value.replace(/\D/g, '');

    if (phone.length < 10) {
      if (!isEdit) {
        form.setFieldsValue({ 'parent.name': '', 'parent.email': '', 'parent.address': '' });
      }
      setAutoFillStatus(null);
      setLastSearchedPhone('');
      return;
    }

    if (phone !== lastSearchedPhone) {
      setLastSearchedPhone(phone);
      setAutoFillStatus('loading');
      try {
        const res     = await childApi.getByParentPhone(phone);
        const matches = res.data || [];
        if (matches.length > 0) {
          const p = matches[0].parent || {};
          const matchedParentId = getRecordId(p);
          const currentParentId = getRecordId(initialValues?.parent);
          const isDifferentParent = isEdit && matchedParentId && currentParentId && matchedParentId !== currentParentId;

          // Auto-fill from the matched record either way — the user can still edit these
          // fields, and the save-time confirmation modal is the authoritative decision point.
          form.setFieldsValue({
            'parent.name':    p.name    || '',
            'parent.email':   p.email   || '',
            'parent.address': p.address || '',
          });
          setAutoFillStatus(isDifferentParent ? 'different-parent' : 'filled');
        } else {
          if (!isEdit) {
            form.setFieldsValue({ 'parent.name': '', 'parent.email': '', 'parent.address': '' });
          }
          setAutoFillStatus(null);
        }
      } catch {
        if (!isEdit) {
          form.setFieldsValue({ 'parent.name': '', 'parent.email': '', 'parent.address': '' });
        }
        setAutoFillStatus(null);
      }
    }
  }, [form, lastSearchedPhone, isEdit, initialValues]);

  // ── Submit ────────────────────────────────────────────────────
  const submitWithAction = async (parentAction) => {
    const values = await form.validateFields();
    const {
      'parent.name':    parentName,
      'parent.phone':   parentPhone,
      'parent.email':   parentEmail,
      'parent.address': parentAddress,
      ...rest
    } = values;

    const payload = {
      ...rest,
      school:      schoolReadOnly ? userSchoolId : rest.school,
      dateOfBirth: values.dateOfBirth?.toISOString(),
      parent:      { name: parentName, phone: parentPhone, email: parentEmail, address: parentAddress },
      ...(parentAction ? { parentAction } : {}),
    };

    const newImage    = fileList.find((f) => f.originFileObj);
    const imageRemoved = !!initialValues?.childImage && fileList.length === 0;

    if (newImage || imageRemoved) {
      const formData = new FormData();
      formData.append('name', payload.name);
      formData.append('gender', payload.gender);
      formData.append('dateOfBirth', payload.dateOfBirth);
      formData.append('school', payload.school);
      if (payload.address)       { formData.append('address',       payload.address); }
      if (payload.hobbies)       { formData.append('hobbies',       payload.hobbies); }
      if (payload.favouriteFood) { formData.append('favouriteFood', payload.favouriteFood); }
      if (payload.standard)      { formData.append('standard',      payload.standard); }
      if (payload.section)       { formData.append('section',       payload.section); }
      formData.append('parent', JSON.stringify(payload.parent));
      if (parentAction)      { formData.append('parentAction', parentAction); }
      if (newImage)          { formData.append('childImage', newImage.originFileObj); }
      else if (imageRemoved) { formData.append('childImage', ''); }
      return onSubmit(formData);
    }
    return onSubmit(payload);
  };

  const showParentActionModal = (context) => {
    if (context?.type === 'LINK_EXISTING_PARENT') {
      const { matchedParent, matchedParentChildren = [] } = context;
      Modal.confirm({
        title: 'Link to existing parent?',
        zIndex: 1050,
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        content: (
          <div style={{ paddingTop: 4 }}>
            <p style={{ marginBottom: 8 }}>
              This mobile number already belongs to <strong>{matchedParent.name}</strong>, parent of:
            </p>
            <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
              {matchedParentChildren.map((c) => <li key={c.id}>{c.name}</li>)}
            </ul>
            <p style={{ marginBottom: 0 }}>
              This child will be linked to that existing parent record. Continue?
            </p>
          </div>
        ),
        okText: 'Link to existing parent',
        cancelText: 'Cancel',
        onOk: () => submitWithAction('linkExisting'),
      });
      return;
    }

    if (context?.type === 'SHARED_PARENT_EDIT') {
      setSharedParentContext(context);
      return;
    }
  };

  const handleSubmit = async () => {
    try {
      await submitWithAction();
    } catch (err) {
      if (err?.errorCode === 'PARENT_ACTION_REQUIRED') {
        showParentActionModal(err.errors);
      }
      // Other errors: the page-level mutation's onError already surfaced a message.
    }
  };

  const bodyProps = {
    form, hideSchool, schoolReadOnly, schoolName, schoolOptions, schoolsLoading,
    selectedDob, setSelectedDob, autoFillStatus, handleParentPhoneChange,
    fileList, fileInputRef, previewOpen, previewSrc, setPreviewOpen,
    beforeUpload: handleImageBeforeUpload, handleDrop: handleImageDrop,
    handlePreview: handleImagePreview, onFileSelect: validateAndSetImage,
    onRemoveFile: handleImageRemove,
  };

  const sharedParentModal = (
    <Modal
      title="This parent record is shared"
      open={!!sharedParentContext}
      zIndex={1050}
      onCancel={() => setSharedParentContext(null)}
      footer={[
        <Button key="cancel" onClick={() => setSharedParentContext(null)}>Cancel</Button>,
        <Button key="createNew" onClick={() => { setSharedParentContext(null); submitWithAction('createNew'); }}>
          Create separate record
        </Button>,
        <Button key="updateShared" type="primary" onClick={() => { setSharedParentContext(null); submitWithAction('updateShared'); }}>
          Apply to all linked children
        </Button>,
      ]}
    >
      {sharedParentContext && (
        <div style={{ paddingTop: 4 }}>
          <p style={{ marginBottom: 8 }}>
            <strong>{sharedParentContext.currentParent.name}</strong>'s parent record is currently linked to:
          </p>
          <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
            {(sharedParentContext.siblings || []).map((c) => <li key={c.id}>{c.name}</li>)}
          </ul>
          <p style={{ marginBottom: 0 }}>
            Do you want to apply these changes to all linked children, or create a separate parent record for just this child?
          </p>
        </div>
      )}
    </Modal>
  );

  // ── Embedded (inline) mode ────────────────────────────────────
  if (!asModal) {
    const btnLabel = submitText || (initialValues ? 'Save Changes' : 'Add & Register Child');
    return (
      <div>
        <ChildFormBody {...bodyProps} />
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          block
          style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none', marginTop: 4 }}
        >
          {btnLabel}
        </Button>
        {sharedParentModal}
      </div>
    );
  }

  // ── Modal mode (default) ──────────────────────────────────────
  return (
    <>
      <Modal
        title={initialValues ? 'Edit Child' : 'Add Child'}
        open={open}
        onCancel={onClose}
        onOk={handleSubmit}
        confirmLoading={loading}
        width={660}
        destroyOnClose
      >
        <ChildFormBody {...bodyProps} />
      </Modal>
      {sharedParentModal}
    </>
  );
};

export default ChildForm;
